import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  GetFacilityAdmin,
  GetFacilityRoles,
  IFacilityRole,
  PutFacilityAdmin,
} from "api/rpc/2024-04/facilityAdmin/client/admin/admin";

import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import Page from "components/page/Page";
import { StatusCode } from "api/protocols";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { IStaff } from "./FacilityStaffDetails";
import { useTranslation } from "react-i18next";
import { ButtonNew } from "components/buttonNew";
import { isEqualWith, isNull } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { errorMessage, isPositiveInteger } from "helpers/Helpers";
import DataTable from "components/dataTable/DataTable";
import { LocaleCurrency } from "helpers/Locale";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import MessageDisplay from "components/messageDisplay/MessageDisplay";

export default function FacilityStaffDetailsEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore, authStore } = useAppSelector(store => store);

  // States
  const [staffState, setStaffState] = useState<IStaff & { role_ids?: Array<number> }>(undefined);
  const [staffStateBeforeChanges, setStaffStateBeforeChanges] = useState<IStaff>(undefined);
  const isOwnAccount = Number(authStore?.user?.id) === Number(staffState?.id ?? id);

  const [roleState, setRoleState] = useState<IFacilityRole[]>(null);
  const { state: roleModal, updateModal: updateRoleModal, closeModal: closeRoleModal } = useModal<{ selectedRoles: Array<{ id: number; title: string; wage?: string }> }>({ selectedRoles: [] });

  // Use Effects
  useEffect(() => {
    void loadFacilityStaff();
    void loadFacilityRoles();
  }, []);

  // Loaders
  async function loadFacilityStaff() {
    const params = {
      id: id,
      extended: true,
      roles: true,
    };

    const staffRes = await GetFacilityAdmin(params, true);
    if (staffRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error when loading staff member")));
      return;
    }


    // Preset roles
    const roleIds = staffRes?.data[0] ? staffRes?.data[0]?.roles?.map(role => role?.id) : [];

    setStaffState(prev => ({
      ...prev,
      id: staffRes.data[0].id,
      first_name: staffRes.data[0].first_name ?? "",
      last_name: staffRes.data[0].last_name ?? "",
      email: staffRes.data[0].email ?? "",
      username: staffRes.data[0].username ?? "",
      state: staffRes.data[0].state ?? "",
      profile_image_source: staffRes.data[0].profile_image_source,
      phone_number: staffRes.data[0].phone_number,
      role_id: staffRes.data[0].role_id,
      quick_code: "",
      has_quick_code: Boolean(staffRes.data[0]?.has_quick_code),
      permissions: staffRes.data[0].permissions,
      role_ids: roleIds,
      roles: staffRes?.data[0]?.roles,
    }));
  }

  async function loadFacilityRoles() {
    const roleRes = await GetFacilityRoles({}, true);
    if (roleRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading facility roles")));
      return;
    }

    setRoleState(roleRes.data);
  }

  async function updateUserDetails() {
    const enteredPin = staffState?.quick_code?.trim() ?? "";
    const pinIsSet = Boolean(staffState?.has_quick_code) || enteredPin !== "";

    if (enteredPin !== "" && !isPositiveInteger(enteredPin)) {
      dispatch(showError(t("Quick code must be a positive integer")));
      return;
    }

    if (staffState?.permissions?.kiosk_view && !pinIsSet) {
      dispatch(showError(t("A Quick Code is required with Kiosk View enabled.")));
      return;
    }

    const params = {
      id: staffState?.id,
      first_name: staffState.first_name,
      last_name: staffState.last_name,
      username: staffState?.username,
      roles: staffState?.roles,
      email: staffState?.email,
      ...(enteredPin !== "" ? { quick_code: Number(enteredPin) } : {}),
    };

    const updateRes = await PutFacilityAdmin(params, true);

    if (updateRes.status !== StatusCode.OK) {
      if (updateRes.data) {
        dispatch(showError(String(updateRes.data)));
      } else {
        dispatch(showError(errorMessage(updateRes, t("Error updating user information."))));
      }
      return;
    }

    dispatch(showSuccess(t("User succesfully updated")));
    history.back();
  }

  // Handlers
  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setStaffState(prevState => ({ ...prevState, [id]: value }));
  }

  function unsavedChangesExist() {
    if (staffStateBeforeChanges === undefined) {
      if (staffState) {
        setStaffStateBeforeChanges(staffState);
      }
      return false;
    }

    return !isEqualWith(staffStateBeforeChanges, staffState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    setStaffState(staffStateBeforeChanges);
  }

  function handleSelectNewRole(role: Partial<IFacilityRole>) {
    let updatedRoles = roleModal?.selectedRoles
      ? [...roleModal.selectedRoles]
      : [];
    const foundRole = updatedRoles?.find(updatedRole => updatedRole?.id === role?.id);
    if (foundRole) {
      updatedRoles = updatedRoles?.filter(updatedRole => updatedRole?.id !== role?.id);
    } else {
      updatedRoles.push({ id: role?.id, title: role?.title, wage: role?.wage });
    }
    updateRoleModal({ selectedRoles: updatedRoles });
  }

  function handleSelectRole(role: Partial<IFacilityRole>) {
    let updatedRoles = staffState?.roles
      ? [...staffState.roles]
      : [];
    const foundRole = updatedRoles?.find(updatedRole => updatedRole?.id === role?.id);
    if (foundRole) {
      updatedRoles = updatedRoles?.filter(updatedRole => updatedRole?.id !== role?.id);
    } else {
      updatedRoles.push({ id: role?.id, title: role?.title, wage: role?.wage });
    }
    setStaffState(prevState => ({ ...prevState, roles: updatedRoles }));
  }

  function handleAddNewRoles() {
    let updatedRoles = staffState?.roles
      ? [...staffState.roles]
      : [];

    updatedRoles = roleModal?.selectedRoles ? [...updatedRoles, ...roleModal.selectedRoles] : updatedRoles;
    setStaffState(prev => ({ ...prev, roles: updatedRoles }));
    void closeRoleModal();
  }

  function handleWageChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
    const { value } = e.target;
    const updatedRoles = staffState?.roles ? [...staffState.roles] : [];
    const updatedRole = { ...updatedRoles[index], wage: value };
    updatedRoles[index] = updatedRole;
    setStaffState(prev => ({ ...prev, roles: updatedRoles }));
  }

  return (
    <Page
      title={t("Edit Staff Account")}
      breadcrumbs={[
        { label: t("Settings") },
        { label: t("Staff Accounts"), url: "/admin/settings/staff" },
        {
          label: staffState ? `${staffState.first_name} ${staffState.last_name}` : t("Staff account"),
          url: `/admin/settings/staff/${id}`,
        },
        { label: t("Edit Staff Account"), url: `/admin/settings/staff_edit_details/${id}` },
      ]}
    >
      <Card title={t("Contact Information")}>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={staffState?.first_name}
                label={t("First Name")}
                id="first_name"
                onChange={handleInputChange}
                placeholder={t("First Name")}
                disabled={staffState?.state === "disabled"}
              />
              <Input
                value={staffState?.last_name}
                label={t("Last Name")}
                id="last_name"
                onChange={handleInputChange}
                placeholder={t("Last Name")}
                disabled={staffState?.state === "disabled"}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={staffState?.email}
                label={t("Email")}
                id="email"
                onChange={handleInputChange}
                placeholder={t("Email")}
                disabled={staffState?.state === "disabled"}
              />
              <Input
                value={staffState?.username}
                label={t("Username")}
                id="username"
                onChange={handleInputChange}
                placeholder={t("Username")}
                disabled={staffState?.state === "disabled"}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <Card
        title={t("Role & Access")}
        titleActions={
          isOwnAccount
            ? undefined
            : [{ content: t("Add roles"), buttonType: "primary", action: () => updateRoleModal({ isOpen: true, selectedRoles: [] }) }]
        }
      >
        <Card.Section>
          <FormLayout>
            <FormLayout.Group alignStart>
              <div>
                <DataTable columns={[{ label: t("Role"), width: "50%" }, ...(authStore?.user?.permissions?.admin_view_shift_wages ? [{ label: t("Wage"), width: "40%" }] : []), { label: "", width: "10%" }]} loading={!staffState?.roles}>
                  {staffState?.roles?.length === 0 ? <td colSpan={authStore?.user?.permissions?.admin_view_shift_wages ? 3 : 2}><MessageDisplay title={t("No roles set")} content={isOwnAccount ? t("There are no roles set on this account. You can't edit roles or permissions on your own account.") : t("There are no roles set on this account. Select 'Add roles' to add new roles to this account")} icon="magnifying-glass" /></td> : staffState?.roles
                    ?.map((role, index) => {
                      return (
                        <tr key={index}>
                          <td>{role?.title}</td>
                          {authStore?.user?.permissions?.admin_view_shift_wages ? <td>
                            <Input onChange={e => handleWageChange(e, index)} labelHidden value={role?.wage ?? ""} prefix={facilityStore?.facility?.default_currency_symbol} suffix={t("/hr")} placeholder={"0.00"} /></td> : null}
                          <td>
                            {isOwnAccount ? null : (
                              <FontAwesomeIcon
                                icon={faXmark}
                                className="cursor-pointer"
                                onClick={() => handleSelectRole(role)}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </DataTable>
              </div>
              {staffState?.state !== "disabled" && (
                <Input
                  value={staffState?.quick_code ?? ""}
                  label={staffState?.permissions?.kiosk_view ? t("PIN (Required)") : t("PIN (Optional)")}
                  id="quick_code"
                  type="password"
                  autoComplete="new-password"
                  onChange={handleInputChange}
                  placeholder={staffState?.has_quick_code ? t("PIN is set") : t("i.e. 1234")}
                  error={staffState?.permissions?.kiosk_view && !staffState?.has_quick_code && !staffState?.quick_code}
                  helpText={
                    staffState?.has_quick_code
                      ? t("A PIN is already set. Enter a new PIN here to replace it.")
                      : staffState?.permissions?.kiosk_view
                        ? t("A Quick Code is required with Kiosk View enabled.")
                        : t("Staff can enter a PIN to access Point-of-Sale app.")
                  }
                />
              )}
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
      {unsavedChangesExist() ? (
        <div className="flex flex-row justify-end pt-3">
          <ButtonNew type="text" onClick={() => cancelUnsavedChanges()}>
            {t("Cancel")}
          </ButtonNew>
          <ButtonNew type="primary" onClick={() => updateUserDetails()}>
            {t("Save Changes")}
          </ButtonNew>
        </div>
      ) : null}


      <Portal isMounted={roleModal?.isOpen}>
        <Sheet
          open={roleModal?.isOpen}
          title={t("Add roles")}
          size="small"
          onCancel={closeRoleModal}
          onOk={handleAddNewRoles}
          okText={t("Add")}
          okDisabled={roleModal?.selectedRoles?.length === 0}
        >
          <div className="ui-checkbox-group">
            {roleState?.filter(role => !staffState?.roles?.some(staffRole => staffRole?.id === role?.id))?.map((role, index) => {
              const checked = roleModal?.selectedRoles?.some(selectedRole => selectedRole?.id === role?.id);
              return (
                <div
                  key={index}
                  onClick={() => handleSelectNewRole(role)}
                  className={classNames("ui-checkbox-group-item", {
                    "ui-checkbox-group-item_selected": checked,
                  })}
                >
                  <div className="ui-checkbox-group-item-content">
                    <div className="ui-checkbox-group-item-text">
                      <p className="text-semibold">{role.title}</p>
                      {role?.wage && authStore?.user?.permissions?.admin_view_shift_wages ? <p><LocaleCurrency amount={role?.wage} currency={facilityStore?.facility?.default_currency_code} />{t("/hr")}</p> : null}
                    </div>
                  </div>
                  <Checkbox
                    size="medium"
                    checked={checked}
                    onChange={() => handleSelectNewRole(role)}
                  />
                </div>
              );
            })}
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}
